import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Stack,
    Typography,
    Alert
} from '@mui/material';

const DECLINE_OPTIONS = [
    { value: 'MINIMUM_WAGE', label: 'Does not meet minimum wage requirements' },
    { value: 'PROBATION_PERIOD', label: 'Employee is still in their probation period' },
    { value: 'EMPLOYEE_LEAVING', label: 'Employee is leaving the business' },
    { value: 'OTHER', label: 'Other (please specify)' },
];

interface DeclineApplicationDialogProps {
    open: boolean;
    onClose: () => void;
    application: any | null;
    onConfirm: (payload: { reasonCode: string; otherReason?: string }) => Promise<void>;
}

const DeclineApplicationDialog = ({ open, onClose, application, onConfirm }: DeclineApplicationDialogProps) => {
    const [selectedReason, setSelectedReason] = useState('MINIMUM_WAGE');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const requiresCustomReason = selectedReason === 'OTHER';

    const handleSubmit = async () => {
        if (requiresCustomReason && customReason.trim().length < 10) {
            setError('Please provide at least 10 characters for the custom reason.');
            return;
        }

        setError(null);
        setSubmitting(true);
        try {
            await onConfirm({
                reasonCode: selectedReason,
                otherReason: requiresCustomReason ? customReason.trim() : undefined,
            });
            setCustomReason('');
            setSelectedReason('MINIMUM_WAGE');
        } catch (err: any) {
            setError(err?.message || 'Failed to decline application');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Decline Application</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Please let us know why you&apos;re declining this application.
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}

                    <TextField
                        select
                        label="Decline reason"
                        value={selectedReason}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        fullWidth
                    >
                        {DECLINE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    {requiresCustomReason && (
                        <TextField
                            multiline
                            minRows={3}
                            label="Please provide more detail"
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            placeholder="Share a short note about why this application cannot be approved."
                            fullWidth
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} variant="outlined" color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} color="error" variant="contained" disabled={submitting}>
                    {submitting ? 'Declining…' : 'Confirm decline'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeclineApplicationDialog;

